/**
 * PipeworkFlowChart.tsx
 *
 * Visual diagram for Appendix C: Pipework & Flow
 * Clearer, more printable diagram showing:
 * - Small pipe vs large pipe with same volumetric flow
 * - Velocity arrows showing difference
 * - Turbulence/noise callouts
 * - One-line explainer about low return temps
 */

interface PipeworkFlowChartProps {
  width?: number;
  height?: number;
}

export function PipeworkFlowChart({ width = 580, height = 320 }: PipeworkFlowChartProps) {
  // Layout dimensions
  const margin = { top: 30, right: 30, bottom: 50, left: 30 };
  const contentWidth = width - margin.left - margin.right;

  // Pipe dimensions
  const pipeStartX = 40;
  const pipeLength = contentWidth * 0.55;
  const smallPipeHeight = 24;
  const largePipeHeight = 56;
  const pipeGap = 40;

  // Arrow dimensions for flow visualization
  const arrowCount = 8;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="dp-pipework-flow-chart"
      role="img"
      aria-label="Pipework flow comparison: small pipe with high velocity vs large pipe with low velocity"
    >
      <title>
        Same flow rate through different pipe sizes: small pipes have high velocity and turbulence,
        large pipes have low velocity and smooth flow. Lower velocity supports efficient heat pump operation.
      </title>
      <defs>
        <style>{`
          .pipe-label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; font-weight: 600; fill: #1e293b; }
          .pipe-sublabel { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; fill: #64748b; }
          .callout-text { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 10px; fill: #475569; }
          .callout-emphasis { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; font-weight: 600; fill: #1e293b; }
          .footer-text { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; fill: #475569; font-style: italic; }
          .small-pipe { fill: #fef2f2; stroke: #dc2626; stroke-width: 2; }
          .large-pipe { fill: #f0f9ff; stroke: #2563eb; stroke-width: 2; }
          .flow-arrow-small { fill: #dc2626; }
          .flow-arrow-large { fill: #2563eb; }
          .turbulent-path { stroke: #ef4444; stroke-width: 1.5; fill: none; stroke-dasharray: 3,2; }
          .laminar-line { stroke: #60a5fa; stroke-width: 1; fill: none; }
        `}</style>

        {/* Arrow markers */}
        <marker id="arrow-small" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <polygon points="0 0, 8 4, 0 8" className="flow-arrow-small" />
        </marker>
        <marker id="arrow-large" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" className="flow-arrow-large" />
        </marker>
      </defs>

      <g transform={`translate(${margin.left}, ${margin.top})`}>

        {/* ===== SMALL PIPE SECTION ===== */}
        <g>
          {/* Label */}
          <text x={pipeStartX} y={-8} className="pipe-label">
            Small Pipe (8-10mm)
          </text>

          {/* Small pipe rectangle */}
          <rect
            x={pipeStartX}
            y={0}
            width={pipeLength}
            height={smallPipeHeight}
            rx="4"
            className="small-pipe"
          />

          {/* Flow arrows inside - packed tight (high velocity) */}
          {[...Array(arrowCount)].map((_, i) => {
            const x1 = pipeStartX + 15 + i * (pipeLength - 50) / arrowCount;
            const x2 = x1 + 25;
            const y = smallPipeHeight / 2;
            return (
              <line
                key={`small-arrow-${i}`}
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke="#dc2626"
                strokeWidth="2.5"
                markerEnd="url(#arrow-small)"
              />
            );
          })}

          {/* Turbulent wavy lines at edges */}
          <path
            d={`M ${pipeStartX + 8} ${3} Q ${pipeStartX + 12} ${-1}, ${pipeStartX + 16} ${3} T ${pipeStartX + 24} ${3}`}
            className="turbulent-path"
          />
          <path
            d={`M ${pipeStartX + 8} ${smallPipeHeight - 3} Q ${pipeStartX + 12} ${smallPipeHeight + 1}, ${pipeStartX + 16} ${smallPipeHeight - 3} T ${pipeStartX + 24} ${smallPipeHeight - 3}`}
            className="turbulent-path"
          />
        </g>

        {/* Callouts for small pipe */}
        <g transform={`translate(${pipeStartX + pipeLength + 15}, ${smallPipeHeight / 2 - 10})`}>
          <text y={0} className="callout-emphasis">High velocity</text>
          <text y={14} className="callout-text">→ More turbulence</text>
          <text y={28} className="callout-text">→ More noise</text>
        </g>

        {/* ===== LARGE PIPE SECTION ===== */}
        <g transform={`translate(0, ${smallPipeHeight + pipeGap})`}>
          {/* Label */}
          <text x={pipeStartX} y={-8} className="pipe-label">
            Large Pipe (22mm+)
          </text>

          {/* Large pipe rectangle */}
          <rect
            x={pipeStartX}
            y={0}
            width={pipeLength}
            height={largePipeHeight}
            rx="6"
            className="large-pipe"
          />

          {/* Flow arrows inside - spaced out (low velocity) */}
          {[...Array(5)].map((_, i) => {
            const x1 = pipeStartX + 30 + i * (pipeLength - 80) / 4;
            const x2 = x1 + 20;
            const y = largePipeHeight / 2;
            return (
              <line
                key={`large-arrow-${i}`}
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke="#2563eb"
                strokeWidth="2"
                markerEnd="url(#arrow-large)"
              />
            );
          })}

          {/* Laminar flow lines (smooth parallel) */}
          <line
            x1={pipeStartX + 15}
            y1={largePipeHeight / 3}
            x2={pipeStartX + pipeLength - 15}
            y2={largePipeHeight / 3}
            className="laminar-line"
          />
          <line
            x1={pipeStartX + 15}
            y1={largePipeHeight * 2 / 3}
            x2={pipeStartX + pipeLength - 15}
            y2={largePipeHeight * 2 / 3}
            className="laminar-line"
          />
        </g>

        {/* Callouts for large pipe */}
        <g transform={`translate(${pipeStartX + pipeLength + 15}, ${smallPipeHeight + pipeGap + largePipeHeight / 2 - 10})`}>
          <text y={0} className="callout-emphasis">Low velocity</text>
          <text y={14} className="callout-text">→ Smoother flow</text>
          <text y={28} className="callout-text">→ Quieter operation</text>
        </g>

        {/* ===== SAME FLOW INDICATOR ===== */}
        <g transform={`translate(${pipeStartX - 25}, ${(smallPipeHeight + pipeGap + largePipeHeight) / 2})`}>
          <text textAnchor="end" className="pipe-sublabel" transform="rotate(-90)">
            Same flow rate
          </text>
        </g>

        {/* ===== FOOTER EXPLANATION ===== */}
        <g transform={`translate(${contentWidth / 2}, ${smallPipeHeight + pipeGap + largePipeHeight + 35})`}>
          <text textAnchor="middle" className="footer-text">
            Lower velocity supports smoother flow and lower return temps
          </text>
          <text textAnchor="middle" y={16} className="footer-text">
            — essential for heat pump efficiency and condensing boiler performance.
          </text>
        </g>

      </g>
    </svg>
  );
}
